import './PromptsTable.scss';
import SimpleBar from 'simplebar-react';
import { DataTable } from 'primereact/datatable';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { Column } from 'primereact/column';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PromptsData } from '../../../pages/prompt/models/PromptsData';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { ADD, PROJECTS, QUESTIONS } from '../../routes/path';
import { Button } from 'primereact/button';
import { SplitButton } from 'primereact/splitbutton';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import ImportPromptsDialog from '../import-prompts-dialog/ImportPromptsDialog';
import { Tooltip } from 'primereact/tooltip';
import { useAccount } from '../../contexts/account/AccountContexts';
import {
  bulkPauseMonitors,
  bulkRemoveMonitors,
  bulkRunMonitors,
  pauseMonitor,
  runMonitor,
} from '../../services/monitor/prompt/PromptService';
import { PeriodType } from '../../types/prompt/PeriodType';
import { ChatType } from '../../types/monitor/ChatType';
import { getPeriodUrl, prepareAndGetChatType } from '../../helpers/StatsHelpers';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import LimitTooltipComponent from '../common/limit-tooltip/LimitTooltipComponent';
import Title from '../title/Title';
import CustomTooltip from '../common/tooltip/CustomTooltip';
import MoreBtn from '../common/more-btn/MoreBtn';
import useCanManage from '../../hooks/account/UseCanManage';
import { MonitorStatus } from '../../types/prompt/MonitorStatusType';
import { Nullable } from 'primereact/ts-helpers';
import useRemovePrompt from '../../hooks/prompt/UseRemovePrompt';
import { createCustomColumn } from '../../helpers/ColumnHelper';
import { canShowModel } from '../../utils/utils';
import { isModelKeySelected } from '../../helpers/MonitorTypeHelpers';
import { Skeleton } from 'primereact/skeleton';
import { useMutation } from '@tanstack/react-query';
import { Checkbox } from 'primereact/checkbox';
import { Menu } from 'primereact/menu';

export interface PromptGroupItem {
  id: number;
  name: string;
  color: string | null;
}

interface PromptsTableProp {
  tableTitle: string;
  data: PromptsData[] | null;
  range: Nullable<(Date | null)[]>;
  periodType: PeriodType | null;
  chatType: ChatType;
  projectId?: number | undefined;
  additionalClass?: string;
  refreshAfterDelete: () => void;
  loader?: () => void;
  isLoading?: boolean;
  modelsWithoutData?: string[];
  selectedChatTypes?: ChatType[];
  groups?: PromptGroupItem[];
  hasPromptGroupsAddon?: boolean;
  onAssignToGroup?: (promptId: number, groupId: number | null) => void;
  onBulkAssignToGroup?: (promptIds: number[], groupId: number | null) => void;
  renderGroupHeader?: (
    group: PromptGroupItem,
    promptCount: number,
    isCollapsed: boolean,
    onToggleCollapse: () => void
  ) => React.ReactNode;
  onCreateGroup?: () => void;
  showStats?: boolean;
  showKeyword?: boolean;
  // V2 / NEW_STATS only: render a per-question Visibility % column (selected brand's mention
  // rate for the question). Off by default so V1 and the prompts management page are unchanged.
  showVisibility?: boolean;
  headerExtra?: React.ReactNode;
  onReorderGroups?: (orderedIds: number[]) => void;
  groupDisplayMode?: 'sections' | 'accordion';
  stickyHeader?: boolean;
  // When set, the table body scrolls within this height and the column header (thead) stays pinned
  // at the top of that scroll (e.g. '70vh'). Needed because the horizontally-scrollable wrapper
  // prevents a page-level sticky thead — see PromptsTable.scss.
  scrollHeight?: string;
}

// Wraps children in our SimpleBar custom scrollbar when `enabled`, otherwise renders them untouched.
// Gives the prompts table a cross-browser-consistent scrollbar while keeping PrimeReact's frozen
// action column: the PrimeReact scroll wrapper is forced to overflow:visible in SCSS so SimpleBar owns
// the scroll, and both the sticky thead and the frozen column resolve against SimpleBar's scrollport.
const SimpleBarScroll: React.FC<{ enabled: boolean; maxHeight?: string; children: React.ReactNode }> = ({
  enabled,
  maxHeight,
  children,
}) =>
  enabled ? (
    <SimpleBar style={{ maxHeight }} autoHide={false}>
      {children}
    </SimpleBar>
  ) : (
    <>{children}</>
  );

const PromptsTable: React.FC<PromptsTableProp> = ({
  tableTitle,
  data,
  range,
  periodType,
  chatType,
  projectId,
  additionalClass,
  refreshAfterDelete,
  loader,
  isLoading,
  modelsWithoutData,
  selectedChatTypes,
  groups,
  hasPromptGroupsAddon,
  onAssignToGroup,
  onBulkAssignToGroup,
  renderGroupHeader,
  onCreateGroup,
  showStats = true,
  showKeyword = true,
  showVisibility = false,
  headerExtra,
  onReorderGroups,
  groupDisplayMode = 'sections',
  stickyHeader = false,
  scrollHeight,
}) => {
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { canManage } = useCanManage();
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [importDialogVisible, setImportDialogVisible] = useState(false);
  const splitButtonRef = useRef<SplitButton>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());
  const [ungroupedCollapsed, setUngroupedCollapsed] = useState(false);
  const bulkMoveMenuRef = useRef<Menu>(null);
  const moveMenuRefs = useRef<Map<number, Menu>>(new Map());
  const [openMoveMenuId, setOpenMoveMenuId] = useState<number | null>(null);
  const [draggedGroupId, setDraggedGroupId] = useState<number | null>(null);
  const [dragOverGroupId, setDragOverGroupId] = useState<number | null>(null);
  const [dragOverGroupIdForPrompt, setDragOverGroupIdForPrompt] = useState<number | 'untagged' | null>(
    null
  );
  const [draggedPromptId, setDraggedPromptId] = useState<number | null>(null);
  const [expandedAccordionGroups, setExpandedAccordionGroups] = useState<Set<number>>(new Set());
  // Accordion pagination is by "unit" (group-summary rows + ungrouped prompt rows), NOT by every
  // rendered row — so a group's expanded children are always shown in full under it on the same page
  // (never split onto the next page). Driven manually via DataTable `lazy` mode.
  const [accordionFirst, setAccordionFirst] = useState(0);
  const [accordionRows, setAccordionRows] = useState(25);
  // Groups currently playing the close animation: kept in expandedAccordionGroups while the
  // collapse animation plays, then removed by a timer.
  const [collapsingGroups, setCollapsingGroups] = useState<Set<number>>(new Set());
  const collapseTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  // Tracks which group was most recently expanded so accordion-row--enter animation plays once.
  const [recentlyExpandedGroup, setRecentlyExpandedGroup] = useState<number | null>(null);
  const expandAnimTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const groupedContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openMoveMenuId) return;
    const handleScroll = () => {
      const ref = moveMenuRefs.current.get(openMoveMenuId);
      ref?.hide({} as React.SyntheticEvent);
      setOpenMoveMenuId(null);
    };
    const layoutElement = document.querySelector('.layout__right');
    if (layoutElement) {
      layoutElement.addEventListener('scroll', handleScroll);
      return () => layoutElement.removeEventListener('scroll', handleScroll);
    }
  }, [openMoveMenuId]);

  useEffect(() => {
    if (!groupedContentRef.current || !onAssignToGroup) return;
    const rows = groupedContentRef.current.querySelectorAll('tbody tr');
    rows.forEach((tr) => {
      tr.setAttribute('draggable', 'true');
      tr.querySelectorAll('td').forEach((td) => td.setAttribute('draggable', 'true'));
    });
  });

  const showGroups = !!(hasPromptGroupsAddon && Array.isArray(groups) && groups.length > 0);

  const promptsByGroup = useMemo<Map<number | null, PromptsData[]>>(() => {
    if (!showGroups || !data) return new Map();
    const validGroupIds = new Set((groups ?? []).map((g) => g.id));
    const map = new Map<number | null, PromptsData[]>();
    data.forEach((prompt) => {
      const groupId = prompt.promptGroupId;
      const key = groupId != null && validGroupIds.has(groupId) ? groupId : null;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(prompt);
    });
    return map;
  }, [showGroups, data, groups]);

  const isAccordionMode = groupDisplayMode === 'accordion' && showGroups;

  const accordionData = useMemo(() => {
    if (!isAccordionMode || !data || !groups) return null;

    // Numeric average (raw) — used as a hidden `sortField` so columns sort numerically, not lexically.
    const avgFieldNum = (prompts: PromptsData[], field: keyof PromptsData): number | undefined => {
      const vals = prompts.map((p) => parseFloat(p[field] as string)).filter((v) => !isNaN(v));
      if (vals.length === 0) return undefined;
      return vals.reduce((a, b) => a + b, 0) / vals.length;
    };

    const avgField = (prompts: PromptsData[], field: keyof PromptsData): string | undefined => {
      const avg = avgFieldNum(prompts, field);
      if (avg === undefined) return undefined;
      return avg % 1 === 0 ? String(avg) : avg.toFixed(1);
    };

    const makeGroupRow = (id: number, name: string, color: string | null, childPrompts: PromptsData[]) => ({
      _isGroupRow: true as const,
      _groupId: id,
      _groupName: name,
      _groupColor: color,
      _childCount: childPrompts.length,
      _childPrompts: childPrompts,
      id: -(id),
      prompt: name,
      keyword: '',
      searchKeyword: '',
      status: 'ACTIVE' as const,
      projectId: null,
      demoVersion: null,
      isRequiredAttention: false,
      defaultPeriod: 'DAILY' as const,
      hasAiOverview: false,
      averagePosition: avgField(childPrompts, 'averagePosition'),
      averageGPTPosition: avgField(childPrompts, 'averageGPTPosition'),
      averageClaudePosition: avgField(childPrompts, 'averageClaudePosition'),
      averageGeminiPosition: avgField(childPrompts, 'averageGeminiPosition'),
      averageDeepSeekPosition: avgField(childPrompts, 'averageDeepSeekPosition'),
      averagePerplexityPosition: avgField(childPrompts, 'averagePerplexityPosition'),
      averageCopilotPosition: avgField(childPrompts, 'averageCopilotPosition'),
      averageGrokPosition: avgField(childPrompts, 'averageGrokPosition'),
      averageAiOverviewPosition: avgField(childPrompts, 'averageAiOverviewPosition'),
      averageAiModePosition: avgField(childPrompts, 'averageAiModePosition'),
      searchVolume: avgField(childPrompts, 'searchVolume'),
      visibility: avgField(childPrompts, 'visibility'),
      // shareOfVoice on child prompts is a "%"-suffixed string (e.g. "12.3%"); avgField's parseFloat
      // ignores the suffix, so the group average is the plain number (rendered with % in the column).
      shareOfVoice: avgField(childPrompts, 'shareOfVoice'),
      // Numeric twins for sorting the group rows (display fields above are formatted strings).
      averagePositionNum: avgFieldNum(childPrompts, 'averagePosition'),
      visibilityNum: avgFieldNum(childPrompts, 'visibility'),
    });

    const result: any[] = [];
    groups.forEach((group) => {
      const childPrompts = promptsByGroup.get(group.id) || [];
      if (childPrompts.length === 0) return;
      const row = makeGroupRow(group.id, group.name, group.color, childPrompts) as any;
      // Alternate the group-row background by group index (every 2nd non-empty group), so adjacent
      // groups are easier to tell apart. Index-based, NOT table-position-based.
      row._groupEven = result.length % 2 === 1;
      result.push(row);
    });

    return result;
  }, [isAccordionMode, data, groups, promptsByGroup]);

  useEffect(() => {
    if (!isAccordionMode || !accordionData || !globalFilterValue) return;
    setExpandedAccordionGroups(new Set(accordionData.map((row: any) => row._groupId)));
  }, [globalFilterValue, isAccordionMode, accordionData]);

  // Build the interleaved row array for the single-table accordion:
  // Paginatable UNITS: each ungrouped prompt row (first), then one per group-summary row. Expanded
  // child prompts are deliberately NOT units — they ride along with their group (see accordionPageRows),
  // so a group's children never get split across a page break.
  const accordionUnits = useMemo<any[]>(() => {
    if (!isAccordionMode || !accordionData) return [];
    const units: any[] = [];
    // Ungrouped prompts go FIRST (top of the table), then the group-summary rows.
    const ungrouped: PromptsData[] = promptsByGroup.get(null) || [];
    ungrouped.forEach((p) => units.push(p));
    accordionData.forEach((groupRow: any) => units.push(groupRow));
    return units;
  }, [isAccordionMode, accordionData, promptsByGroup]);

  // The rows actually rendered for the current page: the current slice of UNITS with expanded group
  // children interleaved directly after their group-summary row. (No synthetic repeated column header
  // above the ungrouped prompts — the real thead is now sticky, so the duplicate would be redundant.)
  const accordionPageRows = useMemo<any[]>(() => {
    const slice = accordionUnits.slice(accordionFirst, accordionFirst + accordionRows);
    const rows: any[] = [];
    let ungroupedIdx = 0;
    slice.forEach((unit: any) => {
      if (unit._isGroupRow) {
        rows.push(unit);
        if (expandedAccordionGroups.has(unit._groupId)) {
          // Tag each child with its parity WITHIN the group so striping restarts per group and is
          // unaffected by other groups expanding/collapsing (the global nth-child zebra is disabled
          // for the accordion table — see SCSS).
          (unit._childPrompts || []).forEach((p: PromptsData, ci: number) =>
            rows.push({ ...p, _zebraEven: ci % 2 === 1 })
          );
        }
      } else {
        rows.push({ ...unit, _zebraEven: ungroupedIdx % 2 === 1 });
        ungroupedIdx += 1;
      }
    });
    return rows;
  }, [accordionUnits, accordionFirst, accordionRows, expandedAccordionGroups]);

  useEffect(() => {
    const handleScroll = () => {
      try { splitButtonRef.current?.hide(); } catch {}
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  // Clear any pending collapse timers on unmount.
  useEffect(() => {
    const timers = collapseTimers.current;
    const expandTimer = expandAnimTimer;
    return () => {
      timers.forEach((tid) => clearTimeout(tid));
      timers.clear();
      if (expandTimer.current !== null) {
        clearTimeout(expandTimer.current);
        expandTimer.current = null;
      }
    };
  }, []);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPrompts, setSelectedPrompts] = useState<PromptsData[]>([]);
  const hasExplicitModelFilter = searchParams.get('models') !== null;

  const isModelColumnHidden = (modelKey: string): boolean => {
    if (chatType !== 'ALL') return true;
    if (!canShowModel(modelKey, accountResponse?.features || [])) return true;
    if (hasExplicitModelFilter && selectedChatTypes && selectedChatTypes.length > 0) {
      return !isModelKeySelected(modelKey, selectedChatTypes);
    }
    return false;
  };

  const { confirmRemove } = useRemovePrompt({
    refreshAfterDelete,
    loader,
  });
  const bulkPauseMutation = useMutation({
    mutationFn: () =>
      bulkPauseMonitors(
        selectedPrompts.map((p) => p.id!).filter((id): id is number => id !== undefined)
      ),
    onSuccess: () => {
      setSelectedPrompts([]);
      refreshAfterDelete();
    },
    onError: (error: any) => {
      if (error?.response?.data?.error === 'PAUSE_LIMIT_EXCEEDED') {
        confirmDialog({
          message: t('pauseLimitExceeded', {
            currentPaused: error.response.data.currentPaused,
            maxPausedMonitors: error.response.data.maxPausedMonitors,
          }),
          header: t('limitExceeded'),
          acceptLabel: 'OK',
          rejectClassName: 'hide',
        });
      } else {
        console.error('Bulk pause failed');
      }
    },
  });

  const bulkRunMutation = useMutation({
    mutationFn: () =>
      bulkRunMonitors(selectedPrompts.map((p) => p.id!).filter((id): id is number => id !== undefined)),
    onSuccess: () => {
      setSelectedPrompts([]);
      refreshAfterDelete();
    },
    onError: (error: any) => {
      if (error?.response?.data?.error === 'LIMIT_EXCEEDED') {
        confirmDialog({
          message: t('bulkLimitExceeded', {
            currentActive: error.response.data.currentActive,
            maxMonitors: error.response.data.maxMonitors,
          }),
          header: t('limitExceeded'),
          acceptLabel: 'OK',
          rejectClassName: 'hide',
        });
      } else {
        console.error('Bulk run failed');
      }
    },
  });

  const bulkRemoveMutation = useMutation({
    mutationFn: () =>
      bulkRemoveMonitors(
        selectedPrompts.map((p) => p.id!).filter((id): id is number => id !== undefined)
      ),
    onSuccess: () => {
      setSelectedPrompts([]);
      refreshAfterDelete();
    },
    onError: () => console.error('Bulk remove failed'),
  });

  const confirmBulkRemove = () => {
    confirmDialog({
      message: t('areYouSureProceed'),
      header: t('areYouSure'),
      defaultFocus: 'accept',
      accept: () => bulkRemoveMutation.mutate(),
    });
  };

  const confirmBulkPause = () => {
    confirmDialog({
      message: t('confirmPausePrompts'),
      header: t('areYouSure'),
      defaultFocus: 'accept',
      accept: () => bulkPauseMutation.mutate(),
    });
  };

  const toggleMonitor = (id: number | undefined, monitorStatus: MonitorStatus) => {
    if (!id) {
      return;
    }

    if (['ACTIVE'].includes(monitorStatus)) {
      confirmDialog({
        message: t('confirmPausePrompts'),
        header: t('areYouSure'),
        defaultFocus: 'accept',
        accept: () => {
          pauseMonitor(id)
            .catch((error: any) => {
              if (error?.response?.data?.error === 'PAUSE_LIMIT_EXCEEDED') {
                confirmDialog({
                  message: t('pauseLimitExceeded', {
                    currentPaused: error.response.data.currentPaused,
                    maxPausedMonitors: error.response.data.maxPausedMonitors,
                  }),
                  header: t('limitExceeded'),
                  acceptLabel: 'OK',
                  rejectClassName: 'hide',
                });
              } else {
                console.error("Error - can't change status");
              }
            })
            .finally(() => refreshAfterDelete());
        },
      });
    } else if (['PAUSED'].includes(monitorStatus)) {
      runMonitor(id)
        .catch((error: any) => {
          if (error?.response?.data?.error === 'LIMIT_EXCEEDED') {
            confirmDialog({
              message: t('bulkLimitExceeded', {
                currentActive: error.response.data.currentActive,
                maxMonitors: error.response.data.maxMonitors,
              }),
              defaultFocus: 'accept',
              header: t('limitExceeded'),
              acceptLabel: 'OK',
              rejectClassName: 'hide',
            });
          } else {
            console.error("Error - can't change status");
          }
        })
        .finally(() => refreshAfterDelete());
    }
  };

  const statusBodyTemplate = (rowData: PromptsData) => {
    const rawStatus = rowData.status;
    const status = isOutOfRange(rowData.created) ? 'unavailable' : rawStatus.toLowerCase();
    return (
      <div className={`status status--${status}`} data-prompt-id={rowData.id}>
        <div className="status__ring" />
        <div className="status__dot" />
      </div>
    );
  };

  const keywordBodyTemplate = (rowData: PromptsData) => {
    return rowData.keyword ? (
      <span className={`tag tag--second  ${chatType === 'ALL' ? 'tag--short' : ''}`}>
        {rowData.keyword}
      </span>
    ) : (
      '-'
    );
  };

  const promptBodyTemplate = (rowData: PromptsData) => {
    const defaultPeriodType = periodType === null ? rowData.defaultPeriod : periodType;
    const aiOverviewLink = `${PROJECTS}/${projectId}${QUESTIONS}/${rowData.id}/${getPeriodUrl(
      defaultPeriodType
    )}/${prepareAndGetChatType(accountResponse, 'AI_OVERVIEW_NEW_BROWSER').toLowerCase()}`;

    const isRequiredAttentions = rowData.isRequiredAttention;
    const canShowIndicator = rowData && isRequiredAttentions;
    const suffix = rowData?.demoVersion ? ' (Demo)' : '';
    const prompt = rowData.prompt + suffix;

    const tooltipText = isRequiredAttentions ? 'noBrandsTooltip' : 'mixedWarning';
    const indicator = canShowIndicator && accountResponse?.isSuperAdmin && (
      <>
        <span className="material-symbols-outlined" id={`warming-icon-${rowData.id}`}>
          warning
        </span>
        <Tooltip
          target={`#warming-icon-${rowData.id}`}
          content={t(tooltipText)}
          className="tooltip--top"
          position="top"
          style={{ width: '220px' }}
          showDelay={5}
        />
      </>
    );
    const aiOverview = rowData && rowData.hasAiOverview && (
      <>
        <NavLink to={aiOverviewLink}>
          <span className="ico--ai-overview ico--sm" id={`ai-overview-${rowData.id}`} />
        </NavLink>
        <Tooltip
          target={`#ai-overview-${rowData.id}`}
          content={t('aiOverviewAvailable')}
          className="tooltip--top"
          position="top"
          showDelay={5}
        />
      </>
    );

    if (isOutOfRange(rowData.created)) {
      return (
        <div className="cell-warning cell-disable">
          <div id={`disable-prompt-${rowData.id}`}>{prompt}</div> {aiOverview} {indicator}
          <CustomTooltip
            tooltipText={t(isToday(rowData.created) ? 'outOfRangePromptToday' : 'outOfRangePrompt')}
            targetRef={`disable-prompt-${rowData.id}`}
          />
        </div>
      );
    }

    return rowData.status === 'PENDING' ? (
      <div className="cell-warning">
        {prompt}
        {aiOverview}
        {indicator}
      </div>
    ) : (
      <div>
        <div className="cell-warning">
          <NavLink to={getLinkToQuestion(defaultPeriodType, rowData.id)}>{prompt}</NavLink>
          {aiOverview}
          {indicator}
        </div>
      </div>
    );
  };

  const isToday = (created: Date | undefined) => {
    if (!created) {
      return false;
    }

    const today = new Date();
    const createdDate = new Date(created);

    return (
      createdDate.getFullYear() === today.getFullYear() &&
      createdDate.getMonth() === today.getMonth() &&
      createdDate.getDate() === today.getDate()
    );
  };

  const getLinkToQuestion = (defaultPeriodType: PeriodType, id: number | undefined) => {
    if (!id) {
      return '';
    }

    const basePath = `${PROJECTS}/${projectId}${QUESTIONS}/${id}/${getPeriodUrl(
      defaultPeriodType
    )}/${prepareAndGetChatType(accountResponse, 'ALL', chatType.toString()).toLowerCase()}`;
    const modelsParam = searchParams.get('models');
    return modelsParam ? `${basePath}?models=${modelsParam}` : basePath;
  };

  const isOutOfRange = (created: Date | undefined) =>
    range &&
    range[1] &&
    created &&
    new Date(created).getTime() >
      new Date(range[1].getFullYear(), range[1].getMonth(), range[1].getDate(), 23, 59, 59).getTime();

  const actionBodyTemplate = (rowData: PromptsData) => {
    const canEdit =
      !rowData?.demoVersion ||
      (accountResponse !== null && accountResponse.isSuperAdmin && !accountResponse.isLimitedSuperAdmin);
    if (
      !canEdit ||
      (accountResponse !== null && ['BLOCKED', 'DISABLED'].includes(accountResponse?.accountStatus))
    ) {
      const defaultPeriodType = periodType === null ? rowData.defaultPeriod : periodType;

      return (
        <>
          <Button
            id={`stats-btn-${rowData.id}`}
            onClick={() => navigate(getLinkToQuestion(defaultPeriodType, rowData.id))}
            className="action-btn"
            disabled={rowData.status !== 'ACTIVE'}
          >
            <span className="ico--stats ico--xs" />
          </Button>
          <CustomTooltip tooltipText="viewDetails" targetRef={`stats-btn-${rowData.id}`} />
        </>
      );
    }

    return (
      <>
        <MoreBtn children={getMoreBtns(rowData)} rowId={rowData.id || 0} />
        {showGroups && rowData.id && onAssignToGroup && (() => {
          const availableGroupsForRow = Array.isArray(groups)
            ? groups.filter((g) => g.id !== rowData.promptGroupId)
            : [];
          const handleAssign = (groupId: number | null) => {
            const ref = moveMenuRefs.current.get(rowData.id!);
            ref?.hide({} as React.SyntheticEvent);
            onAssignToGroup(rowData.id!, groupId);
          };
          const menuModel = [
            ...(rowData.promptGroupId !== null && rowData.promptGroupId !== undefined
              ? [
                  {
                    template: () => (
                      <div className="move-to-group-menu__item" onClick={() => handleAssign(null)}>
                        <span>{t('untagged')}</span>
                      </div>
                    ),
                  },
                  ...(availableGroupsForRow.length > 0 ? [{ separator: true }] : []),
                ]
              : []),
            ...availableGroupsForRow.map((group) => ({
              template: () => (
                <div className="move-to-group-menu__item" onClick={() => handleAssign(group.id)}>
                  <span
                    className="move-to-group-menu__dot"
                    style={{ backgroundColor: group.color || 'var(--grey-ux-400)' }}
                  />
                  <span
                    id={`move-group-row-${rowData.id}-${group.id}`}
                    className="move-to-group-menu__name"
                  >
                    {group.name}
                  </span>
                  <Tooltip
                    target={`#move-group-row-${rowData.id}-${group.id}`}
                    content={group.name}
                    position="top"
                    showDelay={300}
                  />
                </div>
              ),
            })),
          ];
          return (
            <Menu
              ref={(el) => {
                if (el) moveMenuRefs.current.set(rowData.id!, el);
                else moveMenuRefs.current.delete(rowData.id!);
              }}
              popup
              appendTo={document.body}
              className="move-to-group-menu__popup"
              onHide={() => setOpenMoveMenuId(null)}
              model={menuModel}
            />
          );
        })()}
      </>
    );
  };

  const getMoreBtns = (rowData: PromptsData) => {
    const defaultPeriodType = periodType === null ? rowData.defaultPeriod : periodType;

    return (
      <div className="buttons-list">
        {['PAUSED', 'ACTIVE'].includes(rowData.status) && !isOutOfRange(rowData.created) && (
          <>
            <Button
              id={`stats-btn-${rowData.id}`}
              onClick={() => {
                navigate(getLinkToQuestion(defaultPeriodType, rowData.id));
              }}
              className="action-btn"
            >
              <span className="ico--stats ico--xs" />
            </Button>
            <CustomTooltip tooltipText="viewDetails" targetRef={`stats-btn-${rowData.id}`} />
          </>
        )}
        {['PAUSED'].includes(rowData.status) && (
          <>
            <Button
              onClick={() => toggleMonitor(rowData.id, rowData.status)}
              className="action-btn"
              id={`action-question-${rowData.id}`}
            >
              <span className="ico--run ico--xs" />
            </Button>
            <CustomTooltip tooltipText="run" targetRef={`action-question-${rowData.id}`} />
          </>
        )}
        {rowData.status === 'ACTIVE' && (
          <>
            <Button
              onClick={() => toggleMonitor(rowData.id, rowData.status)}
              className={`action-btn`}
              id={`action-question-${rowData.id}`}
            >
              <span className={`ico--pause ico--xs`} />
            </Button>
            <CustomTooltip tooltipText={'pause'} targetRef={`action-question-${rowData.id}`} />
          </>
        )}
        {showGroups && rowData.id && onAssignToGroup && (
          <>
            <Button
              id={`btn-move-prompt-${rowData.id}`}
              className="action-btn"
              onClick={(e) => {
                const ref = moveMenuRefs.current.get(rowData.id!);
                ref?.toggle(e);
                setOpenMoveMenuId(rowData.id!);
              }}
            >
              <span className="ico--move ico--xs" />
            </Button>
            <CustomTooltip tooltipText="moveToPromptGroup" targetRef={`btn-move-prompt-${rowData.id}`} />
          </>
        )}
        {rowData && rowData.id && (
          <>
            <Button
              onClick={() => confirmRemove(Number(rowData.id))}
              className={`action-btn`}
              id={`remove-question-${rowData.id}`}
            >
              <span className="ico--trash ico--xs" />
            </Button>
            <CustomTooltip
              tooltipText="removeThisQuestion"
              targetRef={`remove-question-${rowData.id}`}
            />
          </>
        )}
      </div>
    );
  };

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters['global'].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderButton = () => {
    if (!accountResponse || ['BLOCKED', 'DISABLED'].includes(accountResponse.accountStatus)) {
      return <></>;
    }

    const isDemoVersion = data?.some((promptData) => promptData.demoVersion);
    const hasAnyPaused = selectedPrompts.some((p) => {
      const current = data?.find((d) => d.id === p.id);
      return (current ?? p).status === 'PAUSED';
    });
    const hasAnyActive = selectedPrompts.some((p) => {
      const current = data?.find((d) => d.id === p.id);
      return (current ?? p).status === 'ACTIVE';
    });
    return (
      <div className="page-box__buttons">
        {headerExtra}
        <div className="page-box__buttons-panel">
          {selectionMode && selectedPrompts.length > 0 && (
            <>
              {hasAnyPaused && (
                <>
                  <Button
                    className="action-btn"
                    onClick={() => bulkRunMutation.mutate()}
                    disabled={bulkRunMutation.isPending}
                    id="bulk-activate-btn"
                  >
                    <span className="ico--run ico--xs" />
                  </Button>
                  <CustomTooltip tooltipText="bulkActivate" targetRef="bulk-activate-btn" />
                </>
              )}
              {hasAnyActive && (
                <>
                  <Button
                    className={`action-btn`}
                    onClick={() => confirmBulkPause()}
                    disabled={bulkPauseMutation.isPending}
                    id="bulk-pause-btn"
                  >
                    <span className="ico--pause ico--xs" />
                  </Button>
                  <CustomTooltip tooltipText={'bulkPause'} targetRef="bulk-pause-btn" />
                </>
              )}
              {showGroups && groups && groups.length > 0 && onBulkAssignToGroup && (
                <>
                  <Button
                    id="bulk-move-group-btn"
                    className="action-btn"
                    onClick={(e) => bulkMoveMenuRef.current?.toggle(e)}
                  >
                    <span className="ico--move ico--xs" />
                  </Button>
                  <CustomTooltip tooltipText="moveToPromptGroup" targetRef="bulk-move-group-btn" />
                  <Menu
                    ref={bulkMoveMenuRef}
                    popup
                    appendTo={document.body}
                    className="move-to-group-menu__popup"
                    model={[
                      {
                        template: (
                          <div className="move-to-group-menu__list">
                            <div
                              className="move-to-group-menu__item"
                              onClick={() => {
                                bulkMoveMenuRef.current?.hide({} as React.SyntheticEvent);
                                onBulkAssignToGroup(
                                  selectedPrompts.map((p) => p.id!).filter(Boolean),
                                  null
                                );
                                setSelectedPrompts([]);
                              }}
                            >
                              <span>{t('untagged')}</span>
                            </div>
                            {groups.length > 0 && <div className="move-to-group-menu__separator" />}
                            {groups.map((group) => (
                              <div
                                key={group.id}
                                className="move-to-group-menu__item"
                                onClick={() => {
                                  bulkMoveMenuRef.current?.hide({} as React.SyntheticEvent);
                                  onBulkAssignToGroup(
                                    selectedPrompts.map((p) => p.id!).filter(Boolean),
                                    group.id
                                  );
                                  setSelectedPrompts([]);
                                }}
                              >
                                <span
                                  className="move-to-group-menu__dot"
                                  style={{ backgroundColor: group.color || 'var(--grey-ux-400)' }}
                                />
                                <span
                                  id={`move-group-bulk-${group.id}`}
                                  className="move-to-group-menu__name"
                                >
                                  {group.name}
                                </span>
                                <Tooltip
                                  target={`#move-group-bulk-${group.id}`}
                                  content={group.name}
                                  position="top"
                                  showDelay={300}
                                />
                              </div>
                            ))}
                          </div>
                        ),
                      },
                    ]}
                  />
                </>
              )}
              <>
                <Button
                  className="action-btn action-btn--danger"
                  onClick={confirmBulkRemove}
                  disabled={bulkRemoveMutation.isPending}
                  id="bulk-remove-btn"
                >
                  <span className="ico--trash ico--xs" />
                </Button>
                <CustomTooltip tooltipText="bulkRemove" targetRef="bulk-remove-btn" />
              </>
            </>
          )}
        </div>
        <div className="input-search-box">
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Search..."
            className="input-search"
          />
        </div>
        {!isDemoVersion && (
          <>
            {accountResponse?.addons?.includes('IMPORT_FROM_CSV') ? (
              <SplitButton
                ref={splitButtonRef}
                className={`btn btn--sm ${!canManage ? 'btn--disabled-tooltip-color' : ''}`}
                onClick={() => navigate(`${PROJECTS}/${projectId}${QUESTIONS}${ADD}`)}
                label={t('addNewQuestion')}
                id="add-prompt"
                icon={<span className="ico--add ico--xs" />}
                disabled={!canManage}
                model={[
                  {
                    label: t('importFromCsv'),
                    icon: <span className="ico--download ico--xs" />,
                    command: () => setImportDialogVisible(true),
                  },
                ]}
              />
            ) : (
              <Button
                className={`btn btn--sm ${!canManage ? 'btn--disabled-tooltip-color' : ''}`}
                onClick={() => {
                  if (canManage) {
                    navigate(`${PROJECTS}/${projectId}${QUESTIONS}${ADD}`);
                  }
                }}
                label={t('addNewQuestion')}
                id="add-prompt"
                icon={<span className="ico--add ico--xs" />}
              />
            )}
            {!canManage && <LimitTooltipComponent id="add-prompt" direction="bottom" />}
          </>
        )}
        {!isDemoVersion && onCreateGroup && (
          <>
            <Button
              className={`btn btn--sm btn--secondary`}
              onClick={onCreateGroup}
              label={t('createPromptGroup')}
              id="btn-create-prompt-group"
              icon={<span className="ico--add ico--xs" />}
            />
            <CustomTooltip tooltipText="createPromptGroupTooltip" targetRef="btn-create-prompt-group" />
          </>
        )}
        {!isDemoVersion && (
          <>
            <Button
              className={`action-btn ${selectionMode ? ' action-btn--active' : ''}`}
              onClick={() => {
                setSelectionMode((prev) => {
                  if (prev) setSelectedPrompts([]);
                  return !prev;
                });
              }}
              id="select-mode-btn"
            >
              <span className="ico--select ico--xs" />
            </Button>
            <CustomTooltip tooltipText="selectModePrompts" targetRef="select-mode-btn" />
          </>
        )}
      </div>
    );
  };

  const searchVolumeBodyTemplate = (rowData: PromptsData) => {
    const additionalPanel =
      accountResponse?.isSuperAdmin && !accountResponse?.isLimitedSuperAdmin ? (
        <div className="additional-panel">{rowData.searchKeyword}</div>
      ) : (
        <></>
      );

    return rowData.searchVolume === undefined ? (
      <>
        <span id={`no-search-volume-${rowData.id}`} className="table__cell-loader" />
        <CustomTooltip
          tooltipText={t('waitingSearchVolume')}
          targetRef={`no-search-volume-${rowData.id}`}
        />
        {additionalPanel}
      </>
    ) : rowData.searchVolume === '<100' ? (
      <div className="search-volume">
        <div
          id={`no-search-volume-${rowData.id}`}
          className="search-volume__value search-volume__value--less"
        >
          {rowData.searchVolume}
        </div>
        <CustomTooltip tooltipText={t('noSearchVolume')} targetRef={`no-search-volume-${rowData.id}`} />
        {additionalPanel}
      </div>
    ) : (
      <div className="search-volume">
        {isNaN(Number(rowData.searchVolume)) ? (
          <>- {additionalPanel}</>
        ) : (
          <div className="search-volume__value search-volume__value--more">
            {Intl.NumberFormat('pl-PL').format(Number(rowData.searchVolume))}
          </div>
        )}
        {additionalPanel}
      </div>
    );
  };

  const positionCellBodyTemplate = (
    rowData: PromptsData,
    field: keyof PromptsData,
    modelKey?: string
  ) => {
    const isModelMissingData = !!modelKey && !!modelsWithoutData && modelsWithoutData.includes(modelKey);

    if (isLoading || isModelMissingData || rowData.status === 'PENDING') {
      return <span className="skeleton--cell" />;
    }

    const val = rowData[field];
    if (!val) return '-';
    const num = parseFloat(val as string);
    if (isNaN(num)) return val;
    return num % 1 === 0 ? String(num) : val;
  };

  const emptyTemplate = () => {
    return (
      <div className="no-results">
        <div className="no-results__icon material-symbols-outlined">search</div>
        <div className="no-results__title">{t('noResultsFound')}</div>
        <div className="no-results__desc">{t('noResultsFoundDesc')}</div>
      </div>
    );
  };

  const emptyGroupTemplate = () => {
    return (
      <div className="no-results no-results--compact">
        <div className="no-results__title">{globalFilterValue ? t('noResultsFound') : t('emptyGroup')}</div>
      </div>
    );
  };

  if (data === null) {
    return (
      <>
        <Title showLoader={true} />

        <div className={`table-content ${additionalClass ? additionalClass : ''}`}>
          <Skeleton className="skeleton--table">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </Skeleton>
        </div>
      </>
    );
  }

  if (!data) {
    return <></>;
  }

  const canDragPrompts = !!(onAssignToGroup && showGroups);

  const handleRowDragStart = (e: React.DragEvent) => {
    if (!canDragPrompts) return;
    const tr = (e.target as HTMLElement).closest('tr');
    if (!tr) return;
    const marker = tr.querySelector<HTMLElement>('[data-prompt-id]');
    if (!marker) return;
    const promptId = marker.dataset.promptId;
    if (!promptId) return;
    e.dataTransfer.setData('application/prompt-id', promptId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedPromptId(Number(promptId));

    const ghost = document.createElement('div');
    ghost.className = 'prompt-drag-ghost';
    const promptData = data?.find((d) => d.id === Number(promptId));
    ghost.textContent = promptData?.prompt || `Prompt #${promptId}`;
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    requestAnimationFrame(() => document.body.removeChild(ghost));
  };

  const renderDataTable = (tableData: PromptsData[], tableKey: string, isGroupChild = false) => (
    <DataTable
      key={tableKey}
      stripedRows
      paginator={true}
      scrollable
      scrollHeight={isGroupChild ? undefined : scrollHeight}
      paginatorClassName="pagination"
      removableSort
      rows={10}
      rowsPerPageOptions={[10, 25, 50]}
      value={tableData}
      tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
      tableClassName={`table ${selectionMode ? 'table--select' : ''}${canDragPrompts ? ' table--draggable-rows' : ''}`}
      className="table-wrapper"
      filters={filters}
      emptyMessage={isGroupChild ? emptyGroupTemplate : emptyTemplate}
      currentPageReportTemplate={t('rowsNumber') + ':'}
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      sortField={'averagePosition'}
      sortOrder={1}
      rowClassName={(rowData: PromptsData) =>
        draggedPromptId && rowData.id === draggedPromptId ? 'prompt-row--dragging' : ''
      }
    >
      {selectionMode && (
        <Column
          exportable={false}
          headerStyle={{ width: '25px' }}
          header={
            <Checkbox
              checked={tableData.length > 0 && tableData.every((r) => selectedPrompts.some((p) => p.id === r.id))}
              onChange={(e) =>
                setSelectedPrompts((prev) =>
                  e.checked
                    ? [...prev.filter((p) => !tableData.some((r) => r.id === p.id)), ...tableData]
                    : prev.filter((p) => !tableData.some((r) => r.id === p.id))
                )
              }
            />
          }
          body={(rowData: PromptsData) => (
            <Checkbox
              checked={selectedPrompts.some((p) => p.id === rowData.id)}
              onChange={(e) => {
                if (e.checked) {
                  setSelectedPrompts((prev) => [...prev, rowData]);
                } else {
                  setSelectedPrompts((prev) => prev.filter((p) => p.id !== rowData.id));
                }
              }}
            />
          )}
        />
      )}
          <Column
            body={statusBodyTemplate}
            header={t('status')}
            headerClassName="table__header"
            style={{ width: '37px' }}
          />
          <Column
            sortable
            field="prompt"
            header={t('prompt')}
            body={promptBodyTemplate}
            headerClassName="table__header"
            style={{ minWidth: '200px', maxWidth: '100%' }}
          />
          {showKeyword && createCustomColumn({
            sortable: true,
            field: 'keyword',
            headerName: 'keyword',
            tooltip: t('keywordTooltip'),
            headerClassName: 'table__header',
            bodyClassName: 'table__cell--search-volume',
            style: { width: '120px' },
            body: keywordBodyTemplate,
          })}
          {showStats && createCustomColumn({
            sortable: true,
            field: 'searchVolume',
            headerName: 'searchVolume',
            tooltip: t('searchVolumeTooltip'),
            headerClassName: 'table__header table__header--center',
            bodyClassName: 'table__cell--search-volume table__cell--center',
            style: { width: '60px' },
            body: searchVolumeBodyTemplate,
            hidden: chatType !== 'ALL',
          })}
          {showStats && (
          <Column
            field={'averagePosition'}
            header={t('averagePosition')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(data) => <>{positionCellBodyTemplate(data, 'averagePosition')}</>}
            style={{ width: '125px' }}
            hidden={chatType === 'ALL'}
          />
          )}
          {showStats && createCustomColumn({
            sortable: true,
            field: 'averagePosition',
            headerName: 'averagePosition',
            tooltip: t('overallPositionTooltip'),
            headerClassName: 'table__header table__header--center',
            bodyClassName: 'table__cell--center',
            style: { width: '60px' },
            body: (rowData: any) => <>{positionCellBodyTemplate(rowData, 'averagePosition')}</>,
            hidden: chatType !== 'ALL',
          })}
          {showStats && showVisibility && (
          <Column
            sortable
            field={'visibility'}
            header={t('visibility')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: PromptsData) => (
              <>{rowData.visibility != null ? `${rowData.visibility}%` : '-'}</>
            )}
            style={{ width: '90px' }}
          />
          )}
          {showStats && (
          <Column
            field={'averageGPTPosition'}
            header={t('gptName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => <>{positionCellBodyTemplate(rowData, 'averageGPTPosition', 'GPT')}</>}
            style={{ width: '35px' }}
            hidden={isModelColumnHidden('GPT')}
          />
          )}
          {showStats && (
          <Column
            field={'averageClaudePosition'}
            header={t('claudeName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averageClaudePosition', 'CLAUDE')}</>
            )}
            style={{ width: '35px' }}
            hidden={isModelColumnHidden('CLAUDE')}
          />
          )}
          {showStats && (
          <Column
            field={'averageGeminiPosition'}
            header={t('geminiName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averageGeminiPosition', 'GEMINI')}</>
            )}
            style={{ width: '35px' }}
            hidden={isModelColumnHidden('GEMINI')}
          />
          )}
          {showStats && (
          <Column
            field={'averageDeepSeekPosition'}
            header={t('deepSeekName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averageDeepSeekPosition', 'DEEP_SEEK')}</>
            )}
            style={{ width: '52px' }}
            hidden={isModelColumnHidden('DEEP_SEEK')}
          />
          )}
          {showStats && (
          <Column
            field={'averagePerplexityPosition'}
            header={t('perplexityName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averagePerplexityPosition', 'PERPLEXITY')}</>
            )}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('PERPLEXITY')}
          />
          )}
          {showStats && (
          <Column
            field={'averageCopilotPosition'}
            header={t('copilotSearchName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averageCopilotPosition', 'COPILOT')}</>
            )}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('COPILOT')}
          />
          )}
          {showStats && (
          <Column
            field={'averageGrokPosition'}
            header={t('grokSearchName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => <>{positionCellBodyTemplate(rowData, 'averageGrokPosition', 'GROK')}</>}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('GROK')}
          />
          )}
          {showStats && (
          <Column
            field={'averageAiOverviewPosition'}
            header={t('aiOverviewName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averageAiOverviewPosition', 'AI_OVERVIEW_NEW')}</>
            )}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('AI_OVERVIEW_NEW')}
          />
          )}
          {showStats && (
          <Column
            field={'averageAiModePosition'}
            header={t('aiModeName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData) => (
              <>{positionCellBodyTemplate(rowData, 'averageAiModePosition', 'AI_MODE')}</>
            )}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('AI_MODE')}
          />
          )}
          {showStats && (
          <Column
            field={'shareOfVoice'}
            header={t('averageShareOfVoice')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '160px' }}
            hidden={chatType === 'ALL'}
          />
          )}
          <Column
            frozen={true}
            alignFrozen="right"
            header={t('action')}
            body={actionBodyTemplate}
            bodyClassName="table__cell--gap"
            headerClassName="table__header table__header--center"
            align={'center'}
            style={{ width: '34px' }}
          />
    </DataTable>
  );

  const handleGroupDragStart = (e: React.DragEvent, groupId: number) => {
    setDraggedGroupId(groupId);
    e.dataTransfer.setData('application/group-id', String(groupId));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleGroupDragOver = (e: React.DragEvent, groupId: number) => {
    if (!e.dataTransfer.types.includes('application/group-id')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverGroupId !== groupId) {
      setDragOverGroupId(groupId);
    }
  };

  const handleGroupDrop = (e: React.DragEvent, targetGroupId: number) => {
    if (!e.dataTransfer.types.includes('application/group-id')) return;
    e.preventDefault();
    setDragOverGroupId(null);
    if (!draggedGroupId || draggedGroupId === targetGroupId || !onReorderGroups || !groups) {
      setDraggedGroupId(null);
      return;
    }
    const ids = groups.map((g) => g.id);
    const fromIndex = ids.indexOf(draggedGroupId);
    const toIndex = ids.indexOf(targetGroupId);
    if (fromIndex === -1 || toIndex === -1) {
      setDraggedGroupId(null);
      return;
    }
    const newIds = [...ids];
    newIds.splice(fromIndex, 1);
    newIds.splice(toIndex, 0, draggedGroupId);
    onReorderGroups(newIds);
    setDraggedGroupId(null);
  };

  const handleGroupDragEnd = () => {
    setDraggedGroupId(null);
    setDragOverGroupId(null);
  };

  const handlePromptDragOver = (e: React.DragEvent, groupId: number | 'untagged') => {
    if (!e.dataTransfer.types.includes('application/prompt-id')) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverGroupIdForPrompt !== groupId) {
      setDragOverGroupIdForPrompt(groupId);
    }
  };

  const handlePromptDrop = (e: React.DragEvent, targetGroupId: number | 'untagged') => {
    const promptIdStr = e.dataTransfer.getData('application/prompt-id');
    if (!promptIdStr || !onAssignToGroup) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverGroupIdForPrompt(null);
    const promptId = Number(promptIdStr);
    const newGroupId = targetGroupId === 'untagged' ? null : targetGroupId;
    onAssignToGroup(promptId, newGroupId);
  };

  const handlePromptDragEnd = () => {
    setDragOverGroupIdForPrompt(null);
    setDraggedPromptId(null);
  };


  const renderAccordionContent = () => {
    if (!accordionData) return null;

    const toggleGroup = (groupId: number) => {
      const isOpen = expandedAccordionGroups.has(groupId);
      if (!isOpen) {
        // Expand: splice children in. Cancel any pending collapse for this group.
        const tid = collapseTimers.current.get(groupId);
        if (tid) {
          clearTimeout(tid);
          collapseTimers.current.delete(groupId);
        }
        setCollapsingGroups((prev) => {
          if (!prev.has(groupId)) return prev;
          const n = new Set(prev);
          n.delete(groupId);
          return n;
        });
        setExpandedAccordionGroups((prev) => {
          const n = new Set(prev);
          n.add(groupId);
          return n;
        });
        setRecentlyExpandedGroup(groupId);
        if (expandAnimTimer.current !== null) clearTimeout(expandAnimTimer.current);
        expandAnimTimer.current = setTimeout(() => {
          setRecentlyExpandedGroup((cur) => (cur === groupId ? null : cur));
          expandAnimTimer.current = null;
        }, 300);
      } else {
        // Collapse: keep in expandedAccordionGroups so reveal animation plays, then unmount (~200ms).
        setCollapsingGroups((prev) => {
          const n = new Set(prev);
          n.add(groupId);
          return n;
        });
        const tid = setTimeout(() => {
          collapseTimers.current.delete(groupId);
          setExpandedAccordionGroups((prev) => {
            const n = new Set(prev);
            n.delete(groupId);
            return n;
          });
          setCollapsingGroups((prev) => {
            const n = new Set(prev);
            n.delete(groupId);
            return n;
          });
        }, 200);
        collapseTimers.current.set(groupId, tid);
      }
    };

    // Group rows are NOT tinted with the tag colour anymore — the tag colour is shown as a small dot
    // next to the group name instead. Group rows just use the neutral `.accordion-group-summary-row`
    // background to read as section headers.
    const reveal = (content: React.ReactNode) => (
      <div className="accordion-reveal"><div className="accordion-reveal__inner">{content}</div></div>
    );

    return (
      <div className={`table-content table-content--accordion${scrollHeight ? ' table-content--simplebar' : ''} ${additionalClass ? additionalClass : ''}`}>
        <div className="accordion-table-frame">
        <SimpleBarScroll enabled={!!scrollHeight} maxHeight={scrollHeight}>
        <DataTable
          scrollable
          value={accordionPageRows}
          dataKey="id"
          tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
          tableClassName="table table--accordion-groups"
          className="table-wrapper table-wrapper--accordion"
          emptyMessage={emptyTemplate}
          rowClassName={(rowData: any) => {
            if (rowData._isHeaderRow) return 'accordion-subheader-row';
            if (rowData._isGroupRow) return `accordion-group-summary-row${rowData._groupEven ? ' accordion-group-summary-row--alt' : ''}`;
            const gid = rowData.promptGroupId;
            // Zebra striping only for UNGROUPED prompts (gid == null). Prompts inside an expanded
            // accordion group stay white — only the group-summary header row carries colour.
            const zebra = rowData._zebraEven && gid == null ? ' accordion-child-even' : '';
            if (gid != null && collapsingGroups.has(gid)) return `accordion-prompt-row accordion-row--leave${zebra}`;
            if (gid != null && gid === recentlyExpandedGroup) return `accordion-prompt-row accordion-row--enter${zebra}`;
            return `accordion-prompt-row${zebra}`;
          }}
        >
          <Column
            field="prompt"
            header={t('prompt')}
            headerClassName="table__header"
            style={{ minWidth: '200px', maxWidth: '100%' }}
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('prompt')}</span>;
              if (rowData._isGroupRow) {
                return (
                  <div className="accordion-group-row">
                    <span
                      className="accordion-group-row__dot"
                      style={{ backgroundColor: rowData._groupColor || 'var(--grey-ux-400)' }}
                    />
                    <span className="accordion-group-row__name">{rowData._groupName}</span>
                  </div>
                );
              }
              // Status indicator dot moved inline next to the prompt (the standalone Status column
              // was removed from the accordion for a cleaner single-table look).
              return reveal(
                <div className="accordion-prompt-cell">
                  {statusBodyTemplate(rowData as PromptsData)}
                  {promptBodyTemplate(rowData as PromptsData)}
                </div>
              );
            }}
          />
          {showStats && createCustomColumn({
            sortable: false,
            field: 'searchVolume',
            headerName: 'searchVolume',
            tooltip: t('searchVolumeTooltip'),
            headerClassName: 'table__header table__header--center',
            bodyClassName: 'table__cell--search-volume table__cell--center',
            style: { width: '60px' },
            body: (rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('searchVolume')}</span>;
              if (rowData._isGroupRow) return null; // search volume is not aggregated for groups
              return reveal(<>{searchVolumeBodyTemplate(rowData as PromptsData)}</>);
            },
            hidden: chatType !== 'ALL',
          })}
          {showStats && (
          <Column
            field={'averagePosition'}
            header={t('averagePosition')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('averagePosition')}</span>;
              if (rowData._isGroupRow) return <>{rowData.averagePosition || '-'}</>;
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averagePosition')}</>);
            }}
            style={{ width: '125px' }}
            hidden={chatType === 'ALL'}
          />
          )}
          {showStats && createCustomColumn({
            sortable: false,
            field: 'averagePosition',
            headerName: 'averagePosition',
            tooltip: t('overallPositionTooltip'),
            headerClassName: 'table__header table__header--center',
            bodyClassName: 'table__cell--center',
            style: { width: '60px' },
            body: (rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('averagePosition')}</span>;
              if (rowData._isGroupRow) return <>{rowData.averagePosition || '-'}</>;
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averagePosition')}</>);
            },
            hidden: chatType !== 'ALL',
          })}
          {showStats && showVisibility && (
          <Column
            field={'visibility'}
            header={t('visibility')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('visibility')}</span>;
              if (rowData._isGroupRow) { const v = rowData.visibility; return <>{v != null ? `${v}%` : '-'}</>; }
              const v = rowData.visibility;
              return reveal(<>{v != null ? `${v}%` : '-'}</>);
            }}
            style={{ width: '90px' }}
          />
          )}
          {showStats && (
          <Column
            field={'averageGPTPosition'}
            header={t('gptName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('gptName')}</span>;
              if (rowData._isGroupRow) return <>{rowData.averageGPTPosition || '-'}</>;
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageGPTPosition', 'GPT')}</>);
            }}
            style={{ width: '35px' }}
            hidden={isModelColumnHidden('GPT')}
          />
          )}
          {showStats && (
          <Column
            field={'averageClaudePosition'}
            header={t('claudeName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('claudeName')}</span>;
              if (rowData._isGroupRow) return <>{rowData.averageClaudePosition || '-'}</>;
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageClaudePosition', 'CLAUDE')}</>);
            }}
            style={{ width: '35px' }}
            hidden={isModelColumnHidden('CLAUDE')}
          />
          )}
          {showStats && (
          <Column
            field={'averageGeminiPosition'}
            header={t('geminiName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('geminiName')}</span>;
              if (rowData._isGroupRow) return <>{rowData.averageGeminiPosition || '-'}</>;
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageGeminiPosition', 'GEMINI')}</>);
            }}
            style={{ width: '35px' }}
            hidden={isModelColumnHidden('GEMINI')}
          />
          )}
          {showStats && (
          <Column
            field={'averageDeepSeekPosition'}
            header={t('deepSeekName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('deepSeekName')}</span>;
              if (rowData._isGroupRow) return <>{rowData.averageDeepSeekPosition || '-'}</>;
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageDeepSeekPosition', 'DEEP_SEEK')}</>);
            }}
            style={{ width: '52px' }}
            hidden={isModelColumnHidden('DEEP_SEEK')}
          />
          )}
          {showStats && (
          <Column
            field={'averagePerplexityPosition'}
            header={t('perplexityName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('perplexityName')}</span>;
              if (rowData._isGroupRow) return <>{rowData.averagePerplexityPosition || '-'}</>;
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averagePerplexityPosition', 'PERPLEXITY')}</>);
            }}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('PERPLEXITY')}
          />
          )}
          {showStats && (
          <Column
            field={'averageCopilotPosition'}
            header={t('copilotSearchName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('copilotSearchName')}</span>;
              if (rowData._isGroupRow) return <>{rowData.averageCopilotPosition || '-'}</>;
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageCopilotPosition', 'COPILOT')}</>);
            }}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('COPILOT')}
          />
          )}
          {showStats && (
          <Column
            field={'averageGrokPosition'}
            header={t('grokSearchName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('grokSearchName')}</span>;
              if (rowData._isGroupRow) return <>{rowData.averageGrokPosition || '-'}</>;
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageGrokPosition', 'GROK')}</>);
            }}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('GROK')}
          />
          )}
          {showStats && (
          <Column
            field={'averageAiOverviewPosition'}
            header={t('aiOverviewName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('aiOverviewName')}</span>;
              if (rowData._isGroupRow) return <>{rowData.averageAiOverviewPosition || '-'}</>;
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageAiOverviewPosition', 'AI_OVERVIEW_NEW')}</>);
            }}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('AI_OVERVIEW_NEW')}
          />
          )}
          {showStats && (
          <Column
            field={'averageAiModePosition'}
            header={t('aiModeName')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('aiModeName')}</span>;
              if (rowData._isGroupRow) return <>{rowData.averageAiModePosition || '-'}</>;
              return reveal(<>{positionCellBodyTemplate(rowData as PromptsData, 'averageAiModePosition', 'AI_MODE')}</>);
            }}
            style={{ width: '49px' }}
            hidden={isModelColumnHidden('AI_MODE')}
          />
          )}
          {showStats && (
          <Column
            field={'shareOfVoice'}
            header={t('averageShareOfVoice')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('averageShareOfVoice')}</span>;
              if (rowData._isGroupRow) return <>{rowData.shareOfVoice ? `${rowData.shareOfVoice}%` : '-'}</>;
              return reveal(<>{(rowData as PromptsData).shareOfVoice || '-'}</>);
            }}
            style={{ width: '160px' }}
            hidden={chatType === 'ALL'}
          />
          )}
          <Column
            frozen={true}
            alignFrozen="right"
            header={t('action')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--gap"
            align={'center'}
            style={{ width: '34px' }}
            body={(rowData: any) => {
              if (rowData._isHeaderRow) return <span className="accordion-subheader-label">{t('action')}</span>;
              if (rowData._isGroupRow) {
                // While collapsing, flip the chevron immediately (the group is still in
                // expandedAccordionGroups so its children can finish animating out).
                const isExpanded =
                  expandedAccordionGroups.has(rowData._groupId) &&
                  !collapsingGroups.has(rowData._groupId);
                return (
                  <button
                    type="button"
                    aria-label="toggle group"
                    className={`accordion-group-toggle${isExpanded ? ' accordion-group-toggle--expanded' : ''}`}
                    onClick={() => toggleGroup(rowData._groupId)}
                  >
                    <span className="accordion-group-toggle__chevron ico--up ico--md" />
                  </button>
                );
              }
              return reveal(<>{actionBodyTemplate(rowData as PromptsData)}</>);
            }}
          />
        </DataTable>
        </SimpleBarScroll>
        <Paginator
          className="pagination"
          first={accordionFirst}
          rows={accordionRows}
          totalRecords={accordionUnits.length}
          rowsPerPageOptions={[10, 25, 50, 100]}
          currentPageReportTemplate={t('rowsNumber') + ':'}
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          onPageChange={(e: PaginatorPageChangeEvent) => {
            setAccordionFirst(e.first);
            setAccordionRows(e.rows);
          }}
        />
        </div>
      </div>
    );
  };

  const renderGroupedContent = () => {
    const ungroupedPrompts = promptsByGroup.get(null) || [];
    const canDrag = !!onReorderGroups;
    return (
      <div
        className={`table-content ${additionalClass ? additionalClass : ''}`}
        ref={groupedContentRef}
        onDragStart={handleRowDragStart}
        onDragEnd={handlePromptDragEnd}
      >
        {groups!.map((group) => {
          const groupPrompts = promptsByGroup.get(group.id) || [];
          const isCollapsed = collapsedGroups.has(group.id);
          const toggleCollapse = () =>
            setCollapsedGroups((prev) => {
              const next = new Set(prev);
              if (next.has(group.id)) next.delete(group.id);
              else next.add(group.id);
              return next;
            });
          const isDragging = draggedGroupId === group.id;
          const isDragOver = dragOverGroupId === group.id && draggedGroupId !== group.id;
          const isPromptDragOver = dragOverGroupIdForPrompt === group.id;
          return (
            <div
              className={`prompt-group${isDragging ? ' prompt-group--dragging' : ''}${
                isDragOver || isPromptDragOver ? ' prompt-group--drag-over' : ''
              }`}
              key={`prompt-group-${group.id}`}
              onDragOver={(e) => {
                if (canDrag) handleGroupDragOver(e, group.id);
                handlePromptDragOver(e, group.id);
              }}
              onDrop={(e) => {
                if (canDrag) handleGroupDrop(e, group.id);
                handlePromptDrop(e, group.id);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setDragOverGroupIdForPrompt(null);
                }
              }}
            >
              <div
                draggable={canDrag}
                onDragStart={
                  canDrag
                    ? (e) => {
                        e.stopPropagation();
                        handleGroupDragStart(e, group.id);
                      }
                    : undefined
                }
                onDragEnd={canDrag ? handleGroupDragEnd : undefined}
                className={canDrag ? 'prompt-group__header-drag' : undefined}
              >
                {renderGroupHeader ? (
                  renderGroupHeader(group, groupPrompts.length, isCollapsed, toggleCollapse)
                ) : (
                  <div
                    className={`project-group-header${
                      isCollapsed ? ' project-group-header--collapsed' : ''
                    }`}
                    onClick={toggleCollapse}
                  >
                    <div className="project-group-header__left">
                      <span className="ico--up ico--xs project-group-header__chevron" />
                      <span
                        className="project-group-header__dot"
                        style={{ backgroundColor: group.color || 'var(--grey-ux-400)' }}
                      />
                      <span className="project-group-header__name">{group.name}</span>
                      <span className="project-group-header__count">{groupPrompts.length}</span>
                    </div>
                  </div>
                )}
              </div>
              {!isCollapsed && renderDataTable(groupPrompts, `table-prompt-group-${group.id}`, true)}
            </div>
          );
        })}
        {ungroupedPrompts.length > 0 && (
          <div
            className={`prompt-group${
              dragOverGroupIdForPrompt === 'untagged' ? ' prompt-group--drag-over' : ''
            }`}
            onDragOver={(e) => handlePromptDragOver(e, 'untagged')}
            onDrop={(e) => handlePromptDrop(e, 'untagged')}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setDragOverGroupIdForPrompt(null);
              }
            }}
          >
            <div
              className={`project-group-header project-group-header--ungrouped${
                ungroupedCollapsed ? ' project-group-header--collapsed' : ''
              }`}
              onClick={() => setUngroupedCollapsed((prev) => !prev)}
            >
              <div className="project-group-header__left">
                <span className="ico--up ico--xs project-group-header__chevron" />
                <span
                  className="project-group-header__dot"
                  style={{ backgroundColor: 'var(--grey-ux-400)' }}
                />
                <span className="project-group-header__name">{t('untagged')}</span>
                <span className="project-group-header__count">{ungroupedPrompts.length}</span>
              </div>
            </div>
            {!ungroupedCollapsed && renderDataTable(ungroupedPrompts, 'table-prompt-ungrouped', true)}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <ConfirmDialog className="dialog-box" />

      {stickyHeader ? (
        <div className="page-box__filters" data-observe-sticky>
          <Title title={tableTitle} children={renderButton()} />
        </div>
      ) : (
        <Title title={tableTitle} children={renderButton()} />
      )}

      {isAccordionMode ? (
        renderAccordionContent()
      ) : showGroups ? (
        renderGroupedContent()
      ) : (
        <div className={`table-content${scrollHeight ? ' table-content--sticky-head' : ''} ${additionalClass ? additionalClass : ''}`}>
          {renderDataTable(data, 'table-prompts-flat')}
        </div>
      )}

      {projectId && (
        <ImportPromptsDialog
          visible={importDialogVisible}
          onHide={() => setImportDialogVisible(false)}
          projectId={projectId}
          availableSlots={Math.max(
            0,
            (accountResponse?.maxMonitorCount ?? 0) - (accountResponse?.createdMonitorCount ?? 0)
          )}
          onSuccess={() => {
            setImportDialogVisible(false);
            refreshAfterDelete();
          }}
        />
      )}
    </>
  );
};

export default PromptsTable;
